import { Icon } from '@blueprintjs/core'
import { BlueprintIcons_16Id } from '@blueprintjs/icons/lib/esm/generated/16px/blueprint-icons-16'
import { Tooltip } from './ui/tooltip'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import { Params } from '../packages/blueprint-components/src/useTableHooks'
import { ActionBar } from './ActionBar'
import { navStore } from './nav/Nav'
import { RightMenu } from './nav/RightMenu'
import { Button } from './ui/button'

type HorizontalActionsProps = {
    children?: any
    centerActions?: any
    rightActions?: any
    params?: Params
    showContextMenu?: boolean
    showHamburgerMenu?: boolean
    // This should be an array of MenuItems
    extraMenuItems?: JSX.Element[]
}

export const HorizontalActions = observer(
    ({
        children = <span />,
        centerActions,
        rightActions,
        params,
        extraMenuItems,
        showContextMenu = true,
        showHamburgerMenu = true,
    }: HorizontalActionsProps): JSX.Element => {
        const navigate = useNavigate()
        const { loading, hasUser, missingUser } = useUser()
        const [isCursorInActionArea, setIsCursorInActionArea] = useState<boolean>(false)
        const [hoverOnMenuIcon, setHoverOnMenuIcon] = useState<boolean>(false)

        const onMenuHover = () => {
            setHoverOnMenuIcon(true)
            navStore.setOpen(true)
            navStore.setState('hovered')
        }

        useEffect(() => {
            navStore.setOpen(navStore.isPinned)
        }, [navStore.isPinned])

        useEffect(() => {
            const handleMouseMove = (event: MouseEvent) => {
                const { clientX } = event
                const isOutsideActionArea = clientX >= 290
                setIsCursorInActionArea(!isOutsideActionArea)

                if (navStore.open) {
                    if (isOutsideActionArea && !navStore.isPinned) {
                        navStore.setOpen(false)
                    }
                } else if (clientX <= 4) {
                    onMenuHover()
                }
            }
            window.addEventListener('mousemove', handleMouseMove)
            return () => {
                window.removeEventListener('mousemove', handleMouseMove)
            }
        }, [navStore.open, navStore.isPinned])

        const burgerMenuOnClick = () => {
            if (!navStore.isPinned) {
                navStore.setState('pinned')
                navStore.setOpen(true)
            } else {
                navStore.setOpen(false)
            }
        }

        useEffect(() => {
            if (!navStore.open) {
                navStore.setState('hidden')
                setHoverOnMenuIcon(false)
            }
        }, [navStore.open])

        const rightMenu: JSX.Element = useMemo(
            () => <RightMenu params={params ?? {}} extraMenuItems={extraMenuItems} />,
            [params],
        )

        const generateMenuIcon = (): BlueprintIcons_16Id | null => {
            if (hoverOnMenuIcon) {
                return 'double-chevron-right'
            }

            if (navStore.isPinned) {
                if (hoverOnMenuIcon) {
                    return 'double-chevron-left'
                }

                return null
            } else if (navStore.isHovered) {
                return 'double-chevron-right'
            } else {
                if (hoverOnMenuIcon) {
                    return 'double-chevron-right'
                }

                return 'menu'
            }
        }

        const generateTooltipVerbiage = (): string => {
            if (navStore.open) {
                if (navStore.isPinned) {
                    return 'Close sidebar'
                } else {
                    return 'Pin sidebar'
                }
            } else {
                return 'Open and Pin sidebar'
            }
        }
        const handleHover = (event: React.MouseEvent<HTMLDivElement>) => {
            if (!navStore.isHidden) {
                return
            }
            if (event.type === 'mouseenter') {
                onMenuHover()
            } else if (event.type === 'mouseleave') {
                setHoverOnMenuIcon(false)
                if (!isCursorInActionArea) {
                    navStore.setState('hidden')
                }
            }
        }

        const burgerMenu = (
            <div className="relative h-[49px] w-10">
                <Tooltip hoverOpenDelay={300} content={<span>{generateTooltipVerbiage()}</span>}>
                    <div className="absolute left-0 top-[8.5px]">
                        <div
                            id="nav-open"
                            onMouseLeave={handleHover}
                            onMouseEnter={handleHover}
                            className="flex h-8 w-8 items-center justify-center rounded-md cursor-pointer text-gray-800 hover:bg-gray-100"
                            onClick={burgerMenuOnClick}
                        >
                            <Icon
                                className="block"
                                color={hoverOnMenuIcon || navStore.isHovered ? '#3391FF' : ''}
                                icon={generateMenuIcon()}
                            />
                        </div>
                    </div>
                </Tooltip>
            </div>
        )

        return (
            <>
                <ActionBar className="z-20">
                    <div className="flex w-full items-center h-full">
                        <div className="mx-0 flex items-center">
                            {!navStore.isPinned && hasUser && showHamburgerMenu && burgerMenu}
                        </div>
                        <div
                            className={
                                centerActions
                                    ? 'mr-2 grid min-w-0 flex-grow grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4'
                                    : 'mr-2 flex-grow flex justify-between items-center'
                            }
                        >
                            <div className="min-w-0">{children}</div>
                            {centerActions && (
                                <div className="justify-self-center">{centerActions}</div>
                            )}
                            <div className="flex items-center gap-2 justify-self-end">
                                {loading && <Button className="my-1" minimal disabled text=" " />}
                                {rightActions}
                                {hasUser && showContextMenu && rightMenu}
                                {missingUser && (
                                    <div className="flex items-start">
                                        <Button
                                            minimal
                                            text="Login"
                                            onClick={() => navigate('/login')}
                                        />
                                        <Button
                                            minimal
                                            text="Register"
                                            onClick={() => navigate('/register')}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ActionBar>
            </>
        )
    },
)
